import { useCallback, useEffect, useRef } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import {
  accessTokenNeedsRefresh,
  refreshAccessTokenOnce,
} from "../../services/auth-token-refresh";

const REFRESH_CHECK_INTERVAL_MS = 60_000;

function AuthSessionManager() {
  const { state, getAccessToken, refreshAccessToken } = useAuthContext();
  const checkingRef = useRef(false);

  const refreshIfNeeded = useCallback(async () => {
    if (!state.isAuthenticated || checkingRef.current) {
      return;
    }

    checkingRef.current = true;
    try {
      const accessToken = await getAccessToken();
      if (accessTokenNeedsRefresh(accessToken)) {
        await refreshAccessTokenOnce(refreshAccessToken);
      }
    } catch (error) {
      console.error("Unable to refresh the Asgardeo access token:", error);
    } finally {
      checkingRef.current = false;
    }
  }, [getAccessToken, refreshAccessToken, state.isAuthenticated]);

  useEffect(() => {
    if (!state.isAuthenticated) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        void refreshIfNeeded();
      }
    };
    const handleForeground = () => void refreshIfNeeded();

    void refreshIfNeeded();
    const interval = window.setInterval(
      refreshIfNeeded,
      REFRESH_CHECK_INTERVAL_MS
    );
    window.addEventListener("focus", handleForeground);
    window.addEventListener("online", handleForeground);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", handleForeground);
      window.removeEventListener("online", handleForeground);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshIfNeeded, state.isAuthenticated]);

  return null;
}

export default AuthSessionManager;
